/**
 * Oturum sonlanma teşhis günlüğü.
 *
 * Talep: "Kullanıcı bir sebeple sistemden atılıyor, web tarafında sebebini bilmiyoruz."
 * Oturumu bitirebilecek altı ayrı tetikleyici var (hareketsizlik, token exp, API 401,
 * elle çıkış, OIDC çıkışı, App.js auth kapısı) ve bunların yalnız ikisi kullanıcıya
 * bir mesaj gösteriyordu. Sebebi sonradan öğrenmenin de yolu yoktu: çıkış tam reload
 * yaptığı için console temizleniyor, sebep bilgisi ise sessionStorage'a yazıldığından
 * hem reload sonrası bir kez okunup siliniyor hem de SEKMEYE ÖZEL kalıyor.
 *
 * Bu modül her sonlanmayı localStorage'da bir halka tampona yazar. Kayıt çıkış
 * temizliğinden sağ çıkar (utils/session PRESERVED_LOCAL_KEYS içindedir) ve reload'u
 * atlatır — böylece kullanıcı yeniden giriş yaptıktan sonra bile "en son neden
 * atıldın" sorusu cevaplanabilir.
 *
 * Destek/teşhis için kullanıcının tarayıcı konsolundan:  __dsSessionLog()
 */

import { getUsername, getTokenExp } from './auth';

/** Halka tamponun localStorage anahtarı. utils/session bu anahtarı çıkışta korur. */
export const SESSION_LOG_KEY = 'ds_session_log';

// Son N sonlanma tutulur; teşhis için fazlasıyla yeter, kotayı da zorlamaz.
const MAX_ENTRIES = 30;
// Çağrı yığınından saklanacak satır sayısı — tetikleyiciyi görmeye yetecek kadar.
const STACK_LINES = 6;
// React aynı render'ı iki kez çalıştırabildiği (StrictMode) için aynı olayın
// tekrarını bu pencere içinde bastır.
const DEDUPE_WINDOW_MS = 2000;

/** Sonlanmayı tetikleyen kod yolu — "bilinmeyen sebep" vakalarının tek ipucu. */
function callSite() {
  try {
    return String(new Error().stack || '')
      .split('\n')
      .slice(1, 1 + STACK_LINES)
      .map((line) => line.trim())
      .filter((line) => line && !line.includes('sessionLog.js'))
      .join(' | ');
  } catch {
    return '';
  }
}

function read() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SESSION_LOG_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function append(entry) {
  try {
    const entries = read();
    const last = entries[entries.length - 1];
    if (last && last.reason === entry.reason && last.page === entry.page
      && Date.parse(entry.at) - Date.parse(last.at) < DEDUPE_WINDOW_MS) {
      return; // aynı olayın tekrarı
    }
    localStorage.setItem(SESSION_LOG_KEY, JSON.stringify(entries.concat(entry).slice(-MAX_ENTRIES)));
  } catch { /* kota / private mode — teşhis günlüğü çıkışı engellemesin */ }
}

/**
 * Bir oturum sonlanmasını (ya da sonlanmaya yol açan kararı) kaydeder.
 *
 * @param {string} reason Kayıt defterindeki sebep kodu — bkz. SESSION_END_REASONS.
 * @param {object} [detail] Sebebe özel ölçümler (idle süresi, 401 dönen URL, exp farkı…).
 * @returns {object} Yazılan kayıt.
 */
export function recordSessionEvent(reason, detail) {
  const exp = getTokenExp();
  const entry = {
    at: new Date().toISOString(),
    reason: reason || 'unknown',
    user: getUsername(),
    page: `${window.location.pathname}${window.location.search}`,
    // Token'ın kalan ömrü (sn). Negatifse çıkış anında token ZATEN dolmuştu —
    // "hareketsizlik" mi yoksa "süre doldu" mu ayrımını bu alan kesinleştirir.
    tokenExpiresInSec: exp == null ? null : Math.round((exp - Date.now()) / 1000),
    // Sekme arka plandaysa timer'lar kısılmış olabilir; idle ölçümünü yorumlarken gerekir.
    visibility: typeof document !== 'undefined' ? document.visibilityState : null,
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    detail: detail ?? null,
    from: callSite(),
  };
  append(entry);
  // Reload'a kadar konsolda da dursun — geliştirici canlıda bakıyorsa gözüne çarpsın.
  console.warn('[session]', entry.reason, entry);
  return entry;
}

/** Kayıtlı sonlanma günlüğü (eskiden yeniye). */
export function getSessionLog() {
  return read();
}

export function clearSessionLog() {
  try { localStorage.removeItem(SESSION_LOG_KEY); } catch { /* yoksay */ }
}

/** Sebep kodları kayıt defteri — Login ekranı ve destek ekibi aynı listeyi kullanır. */
export const SESSION_END_REASONS = {
  IDLE: 'idle',                 // SessionGuard: bu sekmede hareketsizlik limiti doldu
  EXPIRED: 'expired',           // SessionGuard: JWT "exp" anına ulaşıldı
  API_401: 'api-401',           // authInterceptor: sunucu bir /api/ çağrısına 401 döndü
  MANUAL: 'manual',             // kullanıcı menüden çıkış yaptı
  OIDC_LOGOUT: 'oidc-logout',   // OIDC sağlayıcı üzerinden çıkış
  CROSS_TAB: 'cross-tab',       // BAŞKA bir sekme oturumu kapattı, bu sekme onu izledi
  AUTH_GATE: 'auth-gate',       // App.js: korumalı route'a geçersiz token ile girildi
};

// Destek için: kullanıcı konsola `__dsSessionLog()` yazıp çıktıyı iletebilsin.
if (typeof window !== 'undefined') {
  window.__dsSessionLog = () => {
    const entries = read();
    console.table(entries);
    return entries;
  };
}

export default recordSessionEvent;
